import { sectionRenderer } from '@theme/section-renderer';

const VERSION = '1.0.1';
const REQUEST_EVENT = 'lt:collection-navigation:request';
const VISUAL_TARGET_EVENT = 'lt:collection-navigation:visual-target';
const COMPLETE_EVENT = 'lt:collection-navigation:complete';
const HISTORY_KEY = 'ltCollectionNavigation';
const ALLOWED_TEMPLATE_KEYS = new Set(['default']);
const MAX_SECTIONS = 5;
const REQUEST_TIMEOUT_MS = 10000;
const IMAGE_PREWARM_BUDGET_MS = 220;

let navigationRevision = 0;
let activeController = null;
let activeNavigationMode = null;
let isCommitting = false;
let historyNavigationInProgress = false;
let historySnapshotTimer = null;

function bindCowboyCardSwatches() {
  if (window.__mbCowboyCardsBound) return;
  window.__mbCowboyCardsBound = true;

  document.addEventListener('click', (event) => {
    const target = event.target;
    const swatch =
      target instanceof Element
        ? target.closest('[data-mb-cowboy-swatch]')
        : null;
    if (!swatch) return;

    const card = swatch.closest('[data-mb-cowboy-card]');
    if (!card) return;

    const image = card.querySelector('[data-mb-cowboy-card-image]');
    const link = card.querySelector('[data-mb-cowboy-card-link]');
    const salePrice = card.querySelector('[data-mb-cowboy-sale-price]');
    const comparePrice = card.querySelector(
      '[data-mb-cowboy-compare-price]'
    );
    const imageUrl = swatch.dataset.image;
    const variantUrl = swatch.dataset.url;

    event.preventDefault();
    event.stopPropagation();

    card.querySelectorAll('[data-mb-cowboy-swatch]').forEach((button) => {
      button.classList.toggle('is-active', button === swatch);
    });

    if (link && variantUrl) {
      link.href = variantUrl;
    }

    if (salePrice && swatch.dataset.price) {
      salePrice.textContent = swatch.dataset.price;
    }

    if (comparePrice) {
      if (swatch.dataset.comparePrice) {
        comparePrice.textContent = swatch.dataset.comparePrice;
        comparePrice.hidden = false;
      } else {
        comparePrice.textContent = '';
        comparePrice.hidden = true;
      }
    }

    if (image && imageUrl && image.src !== imageUrl) {
      card.classList.add('is-changing');

      const preload = new Image();
      preload.onload = () => {
        image.src = imageUrl;
        image.removeAttribute('srcset');
        card.classList.remove('is-changing');
      };
      preload.src = imageUrl;
    }
  });
}

bindCowboyCardSwatches();

function normalizeTemplateKey(value) {
  const key = String(value || '').trim();
  return key || 'default';
}

function getToolbarRoot() {
  return document.querySelector('[data-new-filter-toolbar-root]');
}

function getMainContent() {
  return document.getElementById('MainContent');
}

function isCollectionURL(url) {
  return /^\/(?:[a-z]{2}(?:-[A-Z]{2})?\/)?collections\/[^/]+\/?$/i.test(
    url.pathname
  );
}

function normalizePath(pathname) {
  const normalized = String(pathname || '/').replace(/\/+$/, '');
  return normalized || '/';
}

function getRenderedCollectionPath(toolbarRoot = getToolbarRoot()) {
  const canonicalURL = toolbarRoot?.dataset.canonicalUrl;
  if (!canonicalURL) return '';

  try {
    return normalizePath(new URL(canonicalURL, window.location.href).pathname);
  } catch (_error) {
    return '';
  }
}

function getStateObject(state = history.state) {
  return state && typeof state === 'object' && !Array.isArray(state)
    ? state
    : {};
}

function getHistoryEntryType(state = history.state) {
  const entryType = getStateObject(state)?.[HISTORY_KEY]?.entryType;
  return ['collection', 'filters', 'pagination'].includes(entryType)
    ? entryType
    : 'collection';
}

function getLoadedPaginationPages(root) {
  const scope =
    root || getMainContent()?.querySelector('results-list[section-id]');
  if (!scope) return [];

  return Array.from(
    new Set(
      Array.from(
        scope.querySelectorAll('[ref="cards[]"][data-page]')
      )
        .map((card) => Number(card.getAttribute('data-page')))
        .filter((page) => Number.isInteger(page) && page > 0)
    )
  ).sort((left, right) => left - right);
}

function withHistoryData(state, data) {
  return {
    ...getStateObject(state),
    [HISTORY_KEY]: data,
  };
}

function getSectionId(section) {
  return section.id.replace(/^shopify-section-/, '');
}

function buildSectionPlan(toolbarRoot) {
  const main = getMainContent();
  const toolbarSection = toolbarRoot.closest(
    '.shopify-section[id^="shopify-section-"]'
  );
  const resultsList = main?.querySelector('results-list[section-id]');
  const resultsSection = resultsList?.closest(
    '.shopify-section[id^="shopify-section-"]'
  );

  if (!main || !toolbarSection || !resultsSection) {
    throw new Error('The collection section contract is incomplete.');
  }

  const directSections = Array.from(main.children).filter(
    (element) =>
      element instanceof HTMLElement &&
      element.matches('.shopify-section[id^="shopify-section-"]')
  );

  if (
    directSections.length !== 3 ||
    directSections.length > MAX_SECTIONS ||
    !directSections.includes(toolbarSection) ||
    !directSections.includes(resultsSection)
  ) {
    throw new Error('This collection template is not AJAX compatible.');
  }

  const headingSections = directSections.filter(
    (section) => section !== toolbarSection && section !== resultsSection
  );

  if (headingSections.length !== 1) {
    throw new Error('The collection heading section is ambiguous.');
  }

  const sections = directSections.map((element) => ({
    element,
    id: getSectionId(element),
  }));

  const ids = sections.map(({ id }) => id);

  if (new Set(ids).size !== ids.length) {
    throw new Error('The collection section IDs are not unique.');
  }

  return {
    main,
    toolbarRoot,
    toolbarSection,
    toolbarId: getSectionId(toolbarSection),
    resultsSection,
    resultsId: getSectionId(resultsSection),
    headingSection: headingSections[0],
    headingId: getSectionId(headingSections[0]),
    ids,
  };
}

function canNavigate(detail) {
  if (
    window.Shopify?.designMode ||
    typeof window.fetch !== 'function' ||
    typeof window.AbortController !== 'function' ||
    typeof window.DOMParser !== 'function' ||
    typeof window.history?.pushState !== 'function'
  ) {
    return false;
  }

  const toolbarRoot = detail.toolbarRoot;
  const rawDestinationKey = String(
    detail.destinationTemplateKey || ''
  ).trim();
  const sourceKey = normalizeTemplateKey(
    detail.sourceTemplateKey || toolbarRoot?.dataset.collectionTemplateKey
  );
  const destinationKey = normalizeTemplateKey(rawDestinationKey);
  const destinationURL = new URL(detail.destination, window.location.href);

  if (
    !(toolbarRoot instanceof HTMLElement) ||
    !rawDestinationKey ||
    sourceKey !== destinationKey ||
    !ALLOWED_TEMPLATE_KEYS.has(sourceKey) ||
    destinationURL.origin !== window.location.origin ||
    !isCollectionURL(destinationURL)
  ) {
    return false;
  }

  try {
    buildSectionPlan(toolbarRoot);
    return true;
  } catch (_error) {
    return false;
  }
}

function parseSection(sectionId, html) {
  if (typeof html !== 'string' || !html.trim()) {
    throw new Error(`Section ${sectionId} did not render.`);
  }

  const documentFragment = new DOMParser().parseFromString(html, 'text/html');
  const wrapper = documentFragment.getElementById(
    `shopify-section-${sectionId}`
  );

  if (!wrapper) {
    throw new Error(`Section ${sectionId} is missing from the response.`);
  }

  return wrapper;
}

function getCleanResponseURL(responseURL, requestedURL) {
  const finalURL = new URL(responseURL || requestedURL, window.location.href);
  finalURL.searchParams.delete('sections');

  if (
    finalURL.origin !== window.location.origin ||
    !isCollectionURL(finalURL)
  ) {
    throw new Error('The destination redirected outside the collection router.');
  }

  return finalURL;
}

async function fetchDestination(detail, plan, controller) {
  const requestedURL = new URL(detail.destination, window.location.href);
  const sectionURL = new URL(requestedURL);
  sectionURL.searchParams.set('sections', plan.ids.join(','));

  const response = await fetch(sectionURL, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Collection render failed with ${response.status}.`);
  }

  const renderedSections = await response.json();

  if (!renderedSections || typeof renderedSections !== 'object') {
    throw new Error('The collection response is malformed.');
  }

  const parsed = new Map();

  plan.ids.forEach((sectionId) => {
    parsed.set(
      sectionId,
      parseSection(sectionId, renderedSections[sectionId])
    );
  });

  const incomingToolbarSection = parsed.get(plan.toolbarId);
  const incomingToolbarRoot = incomingToolbarSection?.querySelector(
    '[data-new-filter-toolbar-root]'
  );
  const incomingTemplateKey = normalizeTemplateKey(
    incomingToolbarRoot?.dataset.collectionTemplateKey
  );
  const expectedTemplateKey = normalizeTemplateKey(
    detail.destinationTemplateKey
  );

  if (
    !(incomingToolbarRoot instanceof HTMLElement) ||
    incomingTemplateKey !== expectedTemplateKey
  ) {
    throw new Error('The destination collection template does not match.');
  }

  return {
    finalURL: getCleanResponseURL(response.url, requestedURL),
    parsed,
    incomingToolbarRoot,
  };
}

async function fetchResultsPage(finalURL, sectionId, page, controller) {
  const sectionURL = new URL(finalURL);
  sectionURL.searchParams.set('page', String(page));
  sectionURL.searchParams.set('sections', sectionId);

  const response = await fetch(sectionURL, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
    signal: controller.signal,
  });

  if (!response.ok) {
    throw new Error(`Collection page ${page} failed with ${response.status}.`);
  }

  const renderedSections = await response.json();
  return parseSection(sectionId, renderedSections?.[sectionId]);
}

async function restorePaginationRange(payload, plan, detail, controller) {
  const requestedPages = Array.from(
    new Set(
      (detail.targetState?.[HISTORY_KEY]?.loadedPages || [])
        .map(Number)
        .filter((page) => Number.isInteger(page) && page > 0)
    )
  ).sort((left, right) => left - right);

  if (requestedPages.length <= 1) return;
  if (requestedPages.length > 8) {
    throw new Error('The saved collection page range is too large to restore safely.');
  }

  const targetResults = payload.parsed.get(plan.resultsId);
  const targetGrid = targetResults?.querySelector('[ref="grid"]');
  const currentPage = Number(
    targetGrid?.querySelector('[ref="cards[]"][data-page]')?.getAttribute(
      'data-page'
    ) || new URL(payload.finalURL).searchParams.get('page') || 1
  );

  if (!(targetGrid instanceof Element) || !Number.isInteger(currentPage)) {
    throw new Error('The saved collection page range cannot be restored.');
  }

  const pageSections = new Map([[currentPage, targetResults]]);
  const missingPages = requestedPages.filter((page) => page !== currentPage);

  await Promise.all(
    missingPages.map(async (page) => {
      pageSections.set(
        page,
        await fetchResultsPage(
          payload.finalURL,
          plan.resultsId,
          page,
          controller
        )
      );
    })
  );

  if (controller.signal.aborted) {
    throw new DOMException('Navigation aborted', 'AbortError');
  }

  const ownerDocument = targetGrid.ownerDocument;
  const supportNodes = Array.from(targetGrid.childNodes).filter(
    (node) =>
      !(node instanceof Element && node.matches('[ref="cards[]"]'))
  );
  const cards = requestedPages.flatMap((page) => {
    const section = pageSections.get(page);
    if (!section) return [];

    return Array.from(section.querySelectorAll('[ref="grid"] > [ref="cards[]"]')).map(
      (card) => ownerDocument.importNode(card, true)
    );
  });

  if (!cards.length) {
    throw new Error('The saved collection page range rendered no products.');
  }

  targetGrid.replaceChildren(...supportNodes, ...cards);
}

function waitUntil(timestamp, signal) {
  const remaining = Math.max(0, timestamp - performance.now());

  if (remaining === 0) return Promise.resolve(true);
  if (signal.aborted) return Promise.resolve(false);

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(true), remaining);

    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        resolve(false);
      },
      { once: true }
    );
  });
}

function importSection(section) {
  return document.importNode(section, true);
}

function applySavedGridView(section) {
  const grid = section.querySelector('.product-grid');
  if (!(grid instanceof HTMLElement)) return;

  const viewport = window.matchMedia('(min-width: 750px)').matches
    ? 'desktop'
    : 'mobile';
  const savedView = sessionStorage.getItem(`product-grid-view-${viewport}`);

  if (savedView) {
    grid.setAttribute('product-grid-view', savedView);

    section
      .querySelectorAll(
        'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
      )
      .forEach((option) => {
        option.checked = option.value === savedView;
      });
  }
}

function prewarmImage(sourceImage, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const image = new Image();
    const finish = () => resolve();

    image.onload = () => {
      if (typeof image.decode === 'function') {
        image.decode().catch(() => {}).finally(finish);
      } else {
        finish();
      }
    };
    image.onerror = finish;

    signal.addEventListener('abort', finish, { once: true });

    const srcset = sourceImage.getAttribute('srcset');
    const sizes = sourceImage.getAttribute('sizes');
    const src = sourceImage.getAttribute('src');

    if (sizes) image.sizes = sizes;
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;

    if (!src && !srcset) finish();
  });
}

async function prewarmFirstImages(section, signal) {
  const images = Array.from(section.querySelectorAll('img')).slice(0, 6);
  if (!images.length || signal.aborted) return;

  images.forEach((image, index) => {
    image.setAttribute('loading', 'eager');
    if (index < 3) image.setAttribute('fetchpriority', 'high');
  });

  await Promise.race([
    Promise.all(images.map((image) => prewarmImage(image, signal))),
    new Promise((resolve) =>
      window.setTimeout(resolve, IMAGE_PREWARM_BUDGET_MS)
    ),
  ]);
}

function copyDataAttributes(target, source) {
  const ownedPrefixes = [
    'data-collection-',
    'data-page-',
    'data-document-',
    'data-social-',
    'data-canonical-',
    'data-og-',
  ];

  Array.from(target.attributes).forEach((attribute) => {
    if (ownedPrefixes.some((prefix) => attribute.name.startsWith(prefix))) {
      target.removeAttribute(attribute.name);
    }
  });

  Array.from(source.attributes).forEach((attribute) => {
    if (ownedPrefixes.some((prefix) => attribute.name.startsWith(prefix))) {
      target.setAttribute(attribute.name, attribute.value);
    }
  });
}

function validateToolbar(currentRoot, incomingRoot) {
  const currentLinks = currentRoot.querySelectorAll(
    '[data-collection-category-link]'
  );
  const incomingLinks = incomingRoot.querySelectorAll(
    '[data-collection-category-link]'
  );

  if (currentLinks.length !== incomingLinks.length) {
    throw new Error('The collection toolbar changed before commit.');
  }
}

function updateToolbar(currentRoot, incomingRoot, finalURL) {
  validateToolbar(currentRoot, incomingRoot);
  copyDataAttributes(currentRoot, incomingRoot);

  const currentLinks = Array.from(
    currentRoot.querySelectorAll('[data-collection-category-link]')
  );
  const incomingLinks = Array.from(
    incomingRoot.querySelectorAll('[data-collection-category-link]')
  );

  currentLinks.forEach((link, index) => {
    const incomingLink = incomingLinks[index];
    const isActive = incomingLink.getAttribute('aria-current') === 'page';

    link.setAttribute('href', incomingLink.getAttribute('href') || finalURL);

    if (incomingLink.dataset.collectionTemplateKey) {
      link.dataset.collectionTemplateKey =
        incomingLink.dataset.collectionTemplateKey;
    } else {
      delete link.dataset.collectionTemplateKey;
    }

    link.classList.toggle('is-active', isActive);

    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });

  const currentSort = currentRoot.querySelector(
    '.new-collection-filter__sort'
  );
  const incomingSort = incomingRoot.querySelector(
    '.new-collection-filter__sort'
  );

  if (currentSort && incomingSort) {
    currentSort.replaceChildren(
      ...Array.from(incomingSort.childNodes).map((node) =>
        document.importNode(node, true)
      )
    );
  }
}

function setMetaContent(selector, attributeName, attributeValue, value) {
  let element = document.querySelector(selector);

  if (!value) {
    element?.remove();
    return;
  }

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.append(element);
  }

  element.setAttribute('content', value);
}

function updateDocumentMetadata(toolbarRoot, finalURL) {
  const documentTitle = toolbarRoot.dataset.documentTitle;
  const pageTitle = toolbarRoot.dataset.pageTitle;
  const description = toolbarRoot.dataset.documentDescription;
  const socialDescription = toolbarRoot.dataset.socialDescription;
  const canonicalURL = toolbarRoot.dataset.canonicalUrl || finalURL.toString();
  const ogImage = toolbarRoot.dataset.ogImage;
  const ogImageWidth = toolbarRoot.dataset.ogImageWidth;
  const ogImageHeight = toolbarRoot.dataset.ogImageHeight;

  if (documentTitle) document.title = documentTitle;

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.append(canonical);
  }
  canonical.setAttribute('href', canonicalURL);

  setMetaContent('meta[name="description"]', 'name', 'description', description);
  setMetaContent('meta[property="og:url"]', 'property', 'og:url', canonicalURL);
  setMetaContent('meta[property="og:title"]', 'property', 'og:title', pageTitle);
  setMetaContent(
    'meta[property="og:description"]',
    'property',
    'og:description',
    socialDescription
  );
  setMetaContent('meta[property="og:image"]', 'property', 'og:image', ogImage);
  setMetaContent(
    'meta[property="og:image:secure_url"]',
    'property',
    'og:image:secure_url',
    ogImage
  );
  setMetaContent(
    'meta[property="og:image:width"]',
    'property',
    'og:image:width',
    ogImageWidth
  );
  setMetaContent(
    'meta[property="og:image:height"]',
    'property',
    'og:image:height',
    ogImageHeight
  );
  setMetaContent('meta[name="twitter:title"]', 'name', 'twitter:title', pageTitle);
  setMetaContent(
    'meta[name="twitter:description"]',
    'name',
    'twitter:description',
    socialDescription
  );
}

function closeCollectionDrawer() {
  window.__newCollectionFilterOpen = false;
  document.dispatchEvent(
    new CustomEvent('lt:collection-navigation:close-drawer')
  );
  document.documentElement.classList.remove(
    'new-filter-lock-scroll',
    'new-collection-filter-open-global'
  );
}

function setBusy(toolbarRoot, busy, message = '') {
  const main = getMainContent();
  const status = toolbarRoot?.querySelector('[data-collection-navigation-status]');
  const guardedControls = [
    main?.querySelector('facets-form-component'),
    toolbarRoot?.querySelector('.new-collection-filter__sort'),
  ];

  if (main) {
    if (busy) {
      main.setAttribute('aria-busy', 'true');
    } else {
      main.removeAttribute('aria-busy');
    }
  }

  guardedControls.forEach((element) => {
    if (element instanceof HTMLElement) element.inert = busy;
  });

  if (status) status.textContent = message;
}

function updateCurrentHistoryEntry(templateKey) {
  const previousData = getStateObject(history.state)?.[HISTORY_KEY];
  const entryType = getHistoryEntryType();
  const data = {
    ...(previousData && typeof previousData === 'object' ? previousData : {}),
    url: window.location.href,
    templateKey,
    scrollY: window.scrollY,
    entryType,
    loadedPages:
      entryType === 'filters' ? [] : getLoadedPaginationPages(),
  };

  history.replaceState(
    withHistoryData(history.state, data),
    '',
    window.location.href
  );
}

function commitHistory(finalURL, templateKey, historyMode, targetState) {
  if (historyMode === 'push') {
    const currentRoot = getToolbarRoot();
    const renderedPath = getRenderedCollectionPath(currentRoot);

    if (renderedPath === normalizePath(window.location.pathname)) {
      updateCurrentHistoryEntry(
        normalizeTemplateKey(currentRoot?.dataset.collectionTemplateKey)
      );
    }

    history.pushState(
      withHistoryData({}, {
        url: finalURL.toString(),
        templateKey,
        scrollY: window.scrollY,
        entryType: 'collection',
        loadedPages: [],
      }),
      '',
      finalURL
    );
    return;
  }

  if (historyMode === 'none') {
    const restored = targetState?.[HISTORY_KEY];
    const scrollY = Number.isFinite(restored?.scrollY)
      ? restored.scrollY
      : window.scrollY;
    const entryType = getHistoryEntryType(targetState);
    const loadedPages = Array.isArray(restored?.loadedPages)
      ? restored.loadedPages
      : [];

    history.replaceState(
      withHistoryData(history.state, {
        url: finalURL.toString(),
        templateKey,
        scrollY,
        entryType,
        loadedPages,
      }),
      '',
      finalURL
    );
  }
}

function getNavigationDetail(finalURL, templateKey, historyMode) {
  return {
    url: finalURL.toString(),
    templateKey,
    historyMode,
  };
}

function dispatchCommitRefresh(finalURL, templateKey, historyMode) {
  const detail = {
    ...getNavigationDetail(finalURL, templateKey, historyMode),
    immediate: true,
  };

  document.dispatchEvent(new CustomEvent('ajaxSuccess', { detail }));
  document.dispatchEvent(
    new CustomEvent('mb:product-cards:updated', { detail })
  );
}

function dispatchNavigationComplete(finalURL, templateKey, historyMode) {
  document.dispatchEvent(
    new CustomEvent(COMPLETE_EVENT, {
      detail: getNavigationDetail(finalURL, templateKey, historyMode),
    })
  );
}

async function runCollectionTransition(update) {
  if (
    typeof document.startViewTransition !== 'function' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    update();
    return;
  }

  let commitError = null;
  const transition = document.startViewTransition(() => {
    try {
      update();
    } catch (error) {
      commitError = error;
    }
  });

  transition.types?.add('collection-same-template');

  try {
    await transition.updateCallbackDone;
  } catch (error) {
    commitError ||= error;
  }

  if (commitError) transition.skipTransition?.();

  try {
    await transition.finished;
  } catch (_error) {
    // A skipped or superseded visual transition does not invalidate the DOM commit.
  }

  if (commitError) throw commitError;
}

async function commitDestination(detail, plan, payload, revision) {
  if (revision !== navigationRevision) {
    throw new DOMException('Navigation superseded', 'AbortError');
  }

  const incomingHeading = importSection(payload.parsed.get(plan.headingId));
  const incomingResults = importSection(payload.parsed.get(plan.resultsId));
  applySavedGridView(incomingResults);

  const templateKey = normalizeTemplateKey(
    payload.incomingToolbarRoot.dataset.collectionTemplateKey
  );
  const historyMode = detail.historyMode || 'push';

  await runCollectionTransition(() => {
    if (revision !== navigationRevision) {
      throw new DOMException('Navigation superseded', 'AbortError');
    }

    const currentToolbarRoot = getToolbarRoot();
    const currentPlan = currentToolbarRoot
      ? buildSectionPlan(currentToolbarRoot)
      : null;

    if (!currentToolbarRoot || !currentPlan) {
      throw new Error('The current collection DOM changed before commit.');
    }

    validateToolbar(currentToolbarRoot, payload.incomingToolbarRoot);
    isCommitting = true;

    try {
      closeCollectionDrawer();
      sectionRenderer.abortSection(currentPlan.resultsId);
      commitHistory(
        payload.finalURL,
        templateKey,
        historyMode,
        detail.targetState
      );
      currentPlan.headingSection.replaceWith(incomingHeading);
      currentPlan.resultsSection.replaceWith(incomingResults);
      updateToolbar(
        currentToolbarRoot,
        payload.incomingToolbarRoot,
        payload.finalURL
      );
      updateDocumentMetadata(payload.incomingToolbarRoot, payload.finalURL);
      setBusy(currentToolbarRoot, true, `Loading ${detail.label || 'collection'}`);
      dispatchCommitRefresh(payload.finalURL, templateKey, historyMode);
    } finally {
      isCommitting = false;
    }
  });

  if (revision !== navigationRevision) return;

  const currentRoot = getToolbarRoot();
  setBusy(currentRoot, false, `${payload.incomingToolbarRoot.dataset.collectionTitle || 'Collection'} loaded`);
  dispatchNavigationComplete(payload.finalURL, templateKey, historyMode);

  const restoreY = detail.targetState?.[HISTORY_KEY]?.scrollY;
  if (historyMode === 'none' && Number.isFinite(restoreY)) {
    window.scrollTo({ top: restoreY, behavior: 'instant' });
  }

  if (historyMode === 'none') {
    historyNavigationInProgress = false;
    updateCurrentHistoryEntry(templateKey);
  }
}

function nativeNavigate(link, destination) {
  if (link instanceof HTMLElement) {
    link.setAttribute('data-view-transition-type', 'collection-navigation');
    link.setAttribute('data-view-transition-triggered', 'true');
  }

  window.location.assign(destination);
}

async function navigate(detail) {
  const revision = ++navigationRevision;

  if (activeController) activeController.abort('superseded');

  const controller = new AbortController();
  activeController = controller;
  activeNavigationMode = detail.historyMode || 'push';
  historyNavigationInProgress = activeNavigationMode === 'none';
  const toolbarRoot = detail.toolbarRoot || getToolbarRoot();
  const startedAt = Number(detail.startedAt) || performance.now();
  const minimumHandoff = Number(detail.minimumHandoff) || 0;
  let suspendedSectionId = null;
  let sectionLockToken = null;
  const requestTimeout = window.setTimeout(
    () => controller.abort('timeout'),
    REQUEST_TIMEOUT_MS
  );

  setBusy(
    toolbarRoot,
    true,
    `Loading ${detail.label || 'collection'}`
  );

  try {
    const plan = buildSectionPlan(toolbarRoot);
    suspendedSectionId = plan.resultsId;
    sectionLockToken = sectionRenderer.suspendSection(plan.resultsId);
    const handoffReady = waitUntil(
      startedAt + minimumHandoff,
      controller.signal
    );
    const payload = await fetchDestination(detail, plan, controller);
    await restorePaginationRange(payload, plan, detail, controller);
    const incomingResults = payload.parsed.get(plan.resultsId);

    await Promise.all([
      handoffReady,
      prewarmFirstImages(incomingResults, controller.signal),
    ]);

    if (controller.signal.aborted) {
      throw new DOMException('Navigation aborted', 'AbortError');
    }

    window.clearTimeout(requestTimeout);
    await commitDestination(detail, plan, payload, revision);
  } catch (error) {
    const abortReason = controller.signal.reason;
    const shouldExitSilently =
      revision !== navigationRevision ||
      abortReason === 'superseded' ||
      abortReason === 'pagehide';

    if (shouldExitSilently) {
      return;
    }

    setBusy(toolbarRoot, false, 'Opening collection');

    if (detail.historyMode === 'none') {
      window.location.reload();
      return;
    }

    await waitUntil(startedAt + minimumHandoff, controller.signal);

    if (
      revision !== navigationRevision ||
      controller.signal.reason === 'superseded' ||
      controller.signal.reason === 'pagehide'
    ) {
      return;
    }

    if (revision !== navigationRevision) return;

    nativeNavigate(detail.link, detail.destination);
  } finally {
    window.clearTimeout(requestTimeout);

    if (suspendedSectionId && sectionLockToken) {
      sectionRenderer.resumeSection(suspendedSectionId, sectionLockToken);
    }

    if (activeController === controller) {
      activeController = null;
      activeNavigationMode = null;
      historyNavigationInProgress = false;
    }
  }
}

function findLinkForURL(toolbarRoot, url) {
  const targetPath = url.pathname.replace(/\/$/, '');

  return Array.from(
    toolbarRoot.querySelectorAll('[data-collection-category-link]')
  ).find((link) => {
    const linkURL = new URL(link.href, window.location.href);
    return linkURL.pathname.replace(/\/$/, '') === targetPath;
  });
}

function handlePopState(event) {
  if (window.Shopify?.designMode) return;

  const toolbarRoot = getToolbarRoot();
  if (!toolbarRoot) return;

  const destinationURL = new URL(window.location.href);
  if (!isCollectionURL(destinationURL)) return;

  const link = findLinkForURL(toolbarRoot, destinationURL);
  const sourceKey = normalizeTemplateKey(
    toolbarRoot.dataset.collectionTemplateKey
  );
  const destinationKey = normalizeTemplateKey(
    event.state?.[HISTORY_KEY]?.templateKey ||
      link?.dataset.collectionTemplateKey ||
      sourceKey
  );

  const detail = {
    toolbarRoot,
    link,
    destination: destinationURL.toString(),
    sourceTemplateKey: sourceKey,
    destinationTemplateKey: destinationKey,
    label: link?.textContent?.trim() || 'collection',
    minimumHandoff: 0,
    startedAt: performance.now(),
    historyMode: 'none',
    targetState: getStateObject(event.state),
  };

  if (!canNavigate(detail)) {
    window.location.reload();
    return;
  }

  historyNavigationInProgress = true;

  if (link) {
    document.dispatchEvent(
      new CustomEvent(VISUAL_TARGET_EVENT, {
        detail: { toolbarRoot, link },
      })
    );
  }

  void navigate(detail);
}

function handleRequest(event) {
  const detail = event.detail;
  if (!detail || !canNavigate(detail)) return;

  detail.accepted = true;
  void navigate({
    ...detail,
    historyMode: 'push',
  });
}

function cancelPendingNavigation(force = false) {
  if (!activeController || isCommitting) return false;
  if (activeNavigationMode === 'none' && !force) return false;

  navigationRevision += 1;
  activeController.abort('superseded');
  activeController = null;
  activeNavigationMode = null;
  historyNavigationInProgress = false;
  const toolbarRoot = getToolbarRoot();
  const templateKey = normalizeTemplateKey(
    toolbarRoot?.dataset.collectionTemplateKey
  );

  setBusy(toolbarRoot, false, '');
  dispatchNavigationComplete(
    new URL(window.location.href),
    templateKey,
    'none'
  );
  return true;
}

function initializeHistory() {
  if (window.Shopify?.designMode) return;

  const toolbarRoot = getToolbarRoot();
  if (!toolbarRoot) return;

  updateCurrentHistoryEntry(
    normalizeTemplateKey(toolbarRoot.dataset.collectionTemplateKey)
  );
}

function scheduleHistorySnapshot() {
  if (
    window.Shopify?.designMode ||
    historyNavigationInProgress ||
    activeController ||
    isCommitting
  ) {
    return;
  }

  window.clearTimeout(historySnapshotTimer);
  historySnapshotTimer = window.setTimeout(() => {
    const toolbarRoot = getToolbarRoot();
    if (
      !toolbarRoot ||
      historyNavigationInProgress ||
      activeController ||
      isCommitting
    ) {
      return;
    }

    updateCurrentHistoryEntry(
      normalizeTemplateKey(toolbarRoot.dataset.collectionTemplateKey)
    );
  }, 120);
}

function handlePageShow() {
  historyNavigationInProgress = false;
  activeNavigationMode = null;
  setBusy(getToolbarRoot(), false, '');
  initializeHistory();
}

document.addEventListener(REQUEST_EVENT, handleRequest);
window.addEventListener('popstate', handlePopState);
window.addEventListener('scroll', scheduleHistorySnapshot, { passive: true });
window.addEventListener('pagehide', () => activeController?.abort('pagehide'));
window.addEventListener('pageshow', handlePageShow);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHistory, {
    once: true,
  });
} else {
  initializeHistory();
}

window.LTCollectionNavigation = Object.freeze({
  version: VERSION,
  canNavigate,
  cancelPending: cancelPendingNavigation,
});
